import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Package, 
  Plus, 
  Pencil, 
  Trash2, 
  Save,
  RefreshCw,
  Layers,
  ShoppingBag,
  Tag,
  Percent,
  Box,
  Image,
  Search,
  Copy,
  Eye,
  CheckCircle,
  XCircle,
  Upload,
  Globe,
  CheckSquare,
} from "lucide-react";

const API = "/api";

const CURRENCY_SYMBOLS = {
  TRY: "₺",
  USD: "$",
  EUR: "€",
  GBP: "£"
};

const formatPrice = (amount, currencyCode) => {
  const code = (currencyCode || "TRY").toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code] || code;
  return `${parseFloat(amount || 0).toFixed(2)} ${symbol}`;
};

export default function BundlesPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Bundles with pagination
  const [bundles, setBundles] = useState([]);
  const [totalBundles, setTotalBundles] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [dataSource, setDataSource] = useState("");
  const [filterSync, setFilterSync] = useState(""); // "", "synced", "not_synced"
  const [filterStatus, setFilterStatus] = useState(""); // "", "active", "passive"
  
  // Products for selection
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [bundleForm, setBundleForm] = useState({
    name: "",
    description: "",
    bundleType: "fixed",
    discountType: "percent",
    discountValue: 0,
    components: [],
    price: 0,
    compareAtPrice: 0,
    currencyCode: "TRY",
    isActive: true,
    syncToShopify: true,
    quantityPricing: []
  });
  
  // Component selection
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Shopify transfer
  const [selectedBundles, setSelectedBundles] = useState([]);
  const [transferring, setTransferring] = useState(false);
  const [descriptionLang, setDescriptionLang] = useState("tr"); // Dil seçimi: "tr" veya "en"
  const [bulkSyncStatus, setBulkSyncStatus] = useState(null);

  useEffect(() => {
    fetchBundles();
  }, [token, currentPage, searchTerm, filterSync, filterStatus]);

  const fetchBundles = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        page: currentPage,
        limit: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(filterSync && { sync_status: filterSync }),
        ...(filterStatus && { status: filterStatus }),
        ...(forceRefresh && { refresh: "true" })
      });
      const response = await axios.get(`${API}/bundles?${params}`, { timeout: 120000 });
      setBundles(response.data.bundles || []);
      setTotalBundles(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
      setDataSource(response.data.source || "");
      if (forceRefresh) toast.success(`${response.data.total || 0} asorti Nebim'den güncellendi`);
    } catch (error) {
      console.error("Bundles fetch error:", error);
      toast.error("Asortiler yüklenemedi. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      setSearchTerm(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API}/nebim/products?token=${token}`);
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error("Ürünler yüklenemedi");
    } finally {
      setLoadingProducts(false);
    }
  };

  const openAddDialog = () => {
    setEditingBundle(null);
    setBundleForm({
      name: "",
      description: "",
      bundleType: "fixed",
      discountType: "percent",
      discountValue: 10,
      components: [],
      price: 0,
      compareAtPrice: 0,
      isActive: true,
      syncToShopify: true,
      quantityPricing: []
    });
    setDialogOpen(true);
    fetchProducts();
  };

  const openEditDialog = (bundle) => {
    setEditingBundle(bundle);
    setBundleForm({
      name: bundle.name || "",
      description: bundle.description || "",
      bundleType: bundle.bundleType || "fixed",
      discountType: bundle.discountType || "percent",
      discountValue: bundle.discountValue || 0,
      components: bundle.components || [],
      price: bundle.price || 0,
      compareAtPrice: bundle.compareAtPrice || 0,
      currencyCode: bundle.currencyCode || "TRY",
      isActive: bundle.isActive !== false,
      syncToShopify: bundle.syncToShopify !== false,
      quantityPricing: bundle.quantityPricing || []
    });
    setDialogOpen(true);
    fetchProducts();
  };

  const saveBundle = async () => {
    if (!bundleForm.name) {
      toast.error("Paket adı zorunludur");
      return;
    }
    if (bundleForm.components.length < 2) {
      toast.error("En az 2 ürün eklemelisiniz");
      return;
    }

    setSaving(true);
    try {
      if (editingBundle) {
        await axios.put(`${API}/bundles/${editingBundle.id}?token=${token}`, bundleForm);
        toast.success("Paket güncellendi");
      } else {
        await axios.post(`${API}/bundles?token=${token}`, bundleForm);
        toast.success("Paket oluşturuldu");
      }
      setDialogOpen(false);
      fetchBundles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteBundle = async (bundleId) => {
    if (!confirm("Bu paketi silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/bundles/${bundleId}?token=${token}`);
      toast.success("Paket silindi");
      fetchBundles();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const syncBundleToShopify = async (bundleId, quantityPricing = null) => {
    const toastId = toast.loading("Shopify'a aktarılıyor...");
    try {
      const res = await axios.post(`${API}/bundles/${bundleId}/sync-shopify?token=${token}&force=true&proxy_base=${encodeURIComponent(window.location.origin)}`, {
        quantityPricing: quantityPricing,
        descriptionLanguage: descriptionLang
      }, { timeout: 120000 });
      
      toast.dismiss(toastId);
      
      if (res.data?.skipped) {
        toast.info(res.data.message || "Zaten aktarılmış");
        return;
      }
      
      if (res.data?.success) {
        toast.success(res.data.message || "Shopify'a aktarıldı");
      } else {
        toast.error(res.data.message || "Aktarım başarısız - detay için loglara bakın");
      }
      fetchBundles();
    } catch (error) {
      toast.dismiss(toastId);
      const detail = error.response?.data?.detail || error.message || "Bilinmeyen hata";
      toast.error(`Aktarım hatası: ${detail}`);
    }
  };

  // Dialog'dan Shopify'a aktar (miktar fiyatlandırma ile)
  const syncCurrentBundleToShopify = async () => {
    if (!editingBundle) return;
    
    try {
      await axios.post(`${API}/bundles/${editingBundle.id}/sync-shopify?token=${token}`, {
        quantityPricing: bundleForm.quantityPricing,
        descriptionLanguage: descriptionLang
      });
      toast.success("Shopify'a aktarıldı");
      setDialogOpen(false);
      fetchBundles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Aktarım başarısız");
    }
  };

  // Tümünü Shopify'a aktar (arka planda - tüm 5000+ ürün)
  const syncAllToShopify = async () => {
    try {
      const response = await axios.post(
        `${API}/bundles/sync-all-to-shopify?token=${token}&app_base_url=${encodeURIComponent(window.location.origin)}`,
        { descriptionLanguage: descriptionLang, force: false },
        { timeout: 30000 }
      );
      
      if (response.data.total === 0) {
        toast.info("Aktarılacak asorti yok (tümü zaten Shopify'da veya pasif)");
        return;
      }
      
      toast.success(`${response.data.total} asorti arka planda aktarılıyor...`);
      setTransferring(true);
      setBulkSyncStatus({ status: "running", total: response.data.total, completed: 0, success: 0, errors: 0, current_bundle: "" });
      
      // İlerleme takibi başlat
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/bundles/sync-all-status?token=${token}`);
          const status = statusRes.data;
          setBulkSyncStatus(status);
          
          if (status.status === "completed" || status.status === "idle") {
            clearInterval(pollInterval);
            setTransferring(false);
            fetchBundles();
            // Tamamlanma bilgisini göster, status'u silme - hata listesi için tutuyoruz
          }
        } catch (e) {
          // devam
        }
      }, 2000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Toplu aktarım başlatılamadı");
    }
  };
  
  // Toplu aktarımı iptal et
  const cancelBulkSync = async () => {
    try {
      await axios.post(`${API}/bundles/sync-all-cancel?token=${token}`);
      toast.info("İptal isteği gönderildi...");
    } catch (e) {
      toast.error("İptal hatası");
    }
  };

  // Seçilenleri Shopify'a aktar
  const syncSelectedToShopify = async () => {
    if (selectedBundles.length === 0) {
      toast.info("Lütfen aktarılacak paketleri seçin");
      return;
    }
    
    localStorage.removeItem("cancel_sync");
    setTransferring(true);
    let successCount = 0;
    let errorCount = 0;
    let errorNames = [];
    
    for (const bundleId of selectedBundles) {
      if (localStorage.getItem("cancel_sync") === "true") {
        toast.info(`İşlem durduruldu. ${successCount} aktarıldı.`);
        break;
      }
      try {
        const res = await axios.post(`${API}/bundles/${bundleId}/sync-shopify?token=${token}&proxy_base=${encodeURIComponent(window.location.origin)}`, {
          descriptionLanguage: descriptionLang
        }, { timeout: 120000 });
        if (res.data?.skipped) {
          // Zaten aktarılmış, atla
        } else if (res.data?.success) {
          successCount++;
        } else {
          errorCount++;
          errorNames.push(res.data?.message || bundleId);
        }
      } catch (error) {
        errorCount++;
        errorNames.push(error.response?.data?.detail || bundleId);
      }
    }
    
    setTransferring(false);
    setSelectedBundles([]);
    fetchBundles();
    
    if (!localStorage.getItem("cancel_sync")) {
      if (errorCount === 0 && successCount > 0) {
        toast.success(`${successCount} paket Shopify'a aktarıldı`);
      } else if (errorCount > 0) {
        toast.error(`${successCount} başarılı, ${errorCount} hatalı: ${errorNames.slice(0,3).join(", ")}${errorNames.length > 3 ? "..." : ""}`);
      }
    }
  };

  // Bundle seçimi toggle
  const toggleBundleSelection = (bundleId) => {
    setSelectedBundles(prev => 
      prev.includes(bundleId) 
        ? prev.filter(id => id !== bundleId)
        : [...prev, bundleId]
    );
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    if (selectedBundles.length === bundles.length) {
      setSelectedBundles([]);
    } else {
      setSelectedBundles(bundles.map(b => b.id));
    }
  };

  const addComponent = (product) => {
    const existing = bundleForm.components.find(c => c.productCode === product.productCode);
    if (existing) {
      setBundleForm({
        ...bundleForm,
        components: bundleForm.components.map(c => 
          c.productCode === product.productCode 
            ? { ...c, quantity: c.quantity + 1 }
            : c
        )
      });
    } else {
      setBundleForm({
        ...bundleForm,
        components: [...bundleForm.components, {
          productCode: product.productCode,
          productName: product.productName || product.productCode,
          quantity: 1,
          price: product.price || 0,
          image: product.image || ""
        }]
      });
    }
  };

  const removeComponent = (productCode) => {
    setBundleForm({
      ...bundleForm,
      components: bundleForm.components.filter(c => c.productCode !== productCode)
    });
  };

  const updateComponentQuantity = (productCode, quantity) => {
    if (quantity < 1) return;
    setBundleForm({
      ...bundleForm,
      components: bundleForm.components.map(c => 
        c.productCode === productCode ? { ...c, quantity } : c
      )
    });
  };

  const calculateBundlePrice = () => {
    const totalPrice = bundleForm.components.reduce((sum, c) => sum + (c.price * c.quantity), 0);
    let finalPrice = totalPrice;
    
    if (bundleForm.discountType === "percent") {
      finalPrice = totalPrice * (1 - bundleForm.discountValue / 100);
    } else if (bundleForm.discountType === "fixed") {
      finalPrice = totalPrice - bundleForm.discountValue;
    }
    
    return { totalPrice, finalPrice: Math.max(0, finalPrice) };
  };

  const getBundleTypeLabel = (type) => {
    switch (type) {
      case "fixed": return "Sabit Paket";
      case "multipack": return "Çoklu Paket";
      case "mix-match": return "Seç & Karıştır";
      default: return type;
    }
  };

  const getBundleTypeBadgeColor = (type) => {
    switch (type) {
      case "fixed": return "bg-blue-500";
      case "multipack": return "bg-purple-500";
      case "mix-match": return "bg-emerald-500";
      default: return "bg-gray-500";
    }
  };

  const { totalPrice, finalPrice } = calculateBundlePrice();

  return (
    <div className="space-y-6 fade-in" data-testid="bundles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Asorti & Paket Satış</h1>
          <p className="text-slate-500 mt-1">Ürünleri paketler halinde satışa sunun</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-1.5 border rounded-lg px-2 py-1 bg-white" data-testid="desc-lang-selector">
            <Globe className="w-4 h-4 text-slate-400" />
            <Select value={descriptionLang} onValueChange={setDescriptionLang}>
              <SelectTrigger className="w-[100px] h-8 border-0 shadow-none text-sm px-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tr">Türkçe</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={syncSelectedToShopify} 
            disabled={selectedBundles.length === 0 || transferring}
            variant="outline"
            className="border-purple-500 text-purple-600 hover:bg-purple-50"
            data-testid="sync-selected-btn"
          >
            {transferring ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckSquare className="w-4 h-4 mr-2" />}
            Seçilenleri Aktar ({selectedBundles.length})
          </Button>
          <Button 
            onClick={syncAllToShopify} 
            disabled={transferring}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="sync-all-btn"
          >
            {transferring ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {transferring && bulkSyncStatus ? `${bulkSyncStatus.completed}/${bulkSyncStatus.total}` : "Tümünü Aktar"}
          </Button>
          {transferring && (
            <Button onClick={cancelBulkSync} variant="destructive" size="sm">
              <XCircle className="w-4 h-4 mr-1" /> İptal
            </Button>
          )}
          <Button onClick={openAddDialog} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Paket
          </Button>
        </div>
      </div>

      {/* Canlı İlerleme Paneli */}
      {bulkSyncStatus && (bulkSyncStatus.status === "running" || bulkSyncStatus.status === "completed") && (
        <Card className={bulkSyncStatus.status === "completed" ? "border-emerald-300 bg-emerald-50/50" : "border-purple-300 bg-purple-50/50"}>
          <CardContent className="py-4">
            <div className="space-y-3">
              {/* Başlık + Sayaçlar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {bulkSyncStatus.status === "running" ? (
                    <RefreshCw className="w-5 h-5 text-purple-600 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  )}
                  <span className="font-semibold text-sm">
                    {bulkSyncStatus.status === "running" ? "Aktarım Devam Ediyor..." : "Aktarım Tamamlandı"}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-emerald-700 font-medium">{bulkSyncStatus.success} Başarılı</span>
                  {bulkSyncStatus.errors > 0 && (
                    <span className="text-red-600 font-medium">{bulkSyncStatus.errors} Hata</span>
                  )}
                  <span className="text-slate-500">{bulkSyncStatus.completed}/{bulkSyncStatus.total}</span>
                  {bulkSyncStatus.status === "completed" && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setBulkSyncStatus(null)}>
                      Kapat
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-slate-200 rounded-full h-2.5">
                <div 
                  className={`h-2.5 rounded-full transition-all duration-500 ${bulkSyncStatus.status === "completed" ? "bg-emerald-500" : "bg-purple-500"}`}
                  style={{ width: `${bulkSyncStatus.total > 0 ? Math.round((bulkSyncStatus.completed / bulkSyncStatus.total) * 100) : 0}%` }}
                />
              </div>
              
              {/* Şu an aktarılan ürün */}
              {bulkSyncStatus.status === "running" && bulkSyncStatus.current_bundle && (
                <p className="text-xs text-slate-500 truncate">
                  Aktarılıyor: <span className="text-slate-700">{bulkSyncStatus.current_bundle}</span>
                </p>
              )}
              
              {/* Hatalı ürünler listesi (tamamlanınca göster) */}
              {bulkSyncStatus.status === "completed" && bulkSyncStatus.error_bundles && bulkSyncStatus.error_bundles.length > 0 && (
                <div className="mt-2 border border-red-200 rounded-lg bg-white p-3 max-h-48 overflow-y-auto">
                  <p className="text-xs font-semibold text-red-600 mb-2">Aktarılamayan Ürünler ({bulkSyncStatus.error_bundles.length}):</p>
                  <div className="space-y-1">
                    {bulkSyncStatus.error_bundles.map((eb, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs py-1 border-b border-red-50 last:border-0">
                        <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <span className="font-medium text-slate-800">{eb.name}</span>
                          <span className="text-slate-400 ml-1">({eb.id})</span>
                          <p className="text-red-500 truncate">{eb.error}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Layers className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalBundles.toLocaleString('tr-TR')}</p>
                  <p className="text-xs text-slate-500">Toplam Paket</p>
                </div>
              </div>
            </CardContent>
          </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{bundles.filter(b => b.isActive).length}</p>
                <p className="text-xs text-slate-500">Aktif Paket</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ShoppingBag className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{bundles.filter(b => b.synced_to_shopify || b.shopifyProductId).length}</p>
                <p className="text-xs text-slate-500">Shopify'da</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Percent className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  %{bundles.length > 0 ? Math.round(bundles.reduce((s, b) => s + (b.discountValue || 0), 0) / bundles.length) : 0}
                </p>
                <p className="text-xs text-slate-500">Ort. İndirim</p>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
        <div className="ml-4 hidden md:block">
          <Button
            variant="outline"
            onClick={() => fetchBundles(true)}
            disabled={loading}
            data-testid="btn-refresh-bundles"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Nebim'den Yenile
          </Button>
        </div>
      </div>

      {/* Search + Filters */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Paket adı, ID veya barkod ile ara..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
              data-testid="bundle-search-input"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Filtre:</span>
            <Button
              size="sm"
              variant={filterSync === "" ? "default" : "outline"}
              className={`h-7 text-xs ${filterSync === "" ? "bg-slate-800" : ""}`}
              onClick={() => { setFilterSync(""); setCurrentPage(1); }}
            >
              Tümü
            </Button>
            <Button
              size="sm"
              variant={filterSync === "not_synced" ? "default" : "outline"}
              className={`h-7 text-xs ${filterSync === "not_synced" ? "bg-red-600 hover:bg-red-700" : "border-red-300 text-red-600"}`}
              onClick={() => { setFilterSync("not_synced"); setCurrentPage(1); }}
            >
              Aktarılmayan
            </Button>
            <Button
              size="sm"
              variant={filterSync === "synced" ? "default" : "outline"}
              className={`h-7 text-xs ${filterSync === "synced" ? "bg-emerald-600 hover:bg-emerald-700" : "border-emerald-300 text-emerald-600"}`}
              onClick={() => { setFilterSync("synced"); setCurrentPage(1); }}
            >
              Shopify'da
            </Button>
            <div className="w-px h-5 bg-slate-200 mx-1" />
            <Button
              size="sm"
              variant={filterStatus === "active" ? "default" : "outline"}
              className={`h-7 text-xs ${filterStatus === "active" ? "bg-blue-600 hover:bg-blue-700" : "border-blue-300 text-blue-600"}`}
              onClick={() => { setFilterStatus(filterStatus === "active" ? "" : "active"); setCurrentPage(1); }}
            >
              Aktif
            </Button>
            <Button
              size="sm"
              variant={filterStatus === "passive" ? "default" : "outline"}
              className={`h-7 text-xs ${filterStatus === "passive" ? "bg-slate-600 hover:bg-slate-700" : "border-slate-300 text-slate-500"}`}
              onClick={() => { setFilterStatus(filterStatus === "passive" ? "" : "passive"); setCurrentPage(1); }}
            >
              Pasif
            </Button>
            {(filterSync || filterStatus) && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-slate-400 hover:text-slate-600"
                onClick={() => { setFilterSync(""); setFilterStatus(""); setCurrentPage(1); }}
              >
                Temizle
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bundles Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-500" />
            Paket Listesi
          </CardTitle>
          <CardDescription>
            {totalBundles.toLocaleString('tr-TR')} asortiden {bundles.length} tanesi gösteriliyor
            {dataSource === "nebim" && <Badge variant="outline" className="ml-2 text-xs">Nebim</Badge>}
            {totalPages > 1 && <span className="ml-2">| Sayfa {currentPage}/{totalPages}</span>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
              <p className="text-sm text-slate-400">Asortiler Nebim'den yükleniyor...</p>
            </div>
          ) : bundles.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Henüz paket oluşturulmadı</p>
              <Button onClick={openAddDialog} className="mt-4" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                İlk Paketi Oluştur
              </Button>
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={selectedBundles.length === bundles.length && bundles.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Paket Adı</TableHead>
                  <TableHead>Tür</TableHead>
                  <TableHead className="text-center">Ürün Sayısı</TableHead>
                  <TableHead className="text-right">Fiyat</TableHead>
                  <TableHead className="text-center">İndirim</TableHead>
                  <TableHead className="text-center">Durum</TableHead>
                  <TableHead className="text-center">Shopify</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bundles.map((bundle, idx) => (
                  <TableRow key={`${bundle.id}-${idx}`} className={selectedBundles.includes(bundle.id) ? "bg-purple-50" : ""}>
                    <TableCell>
                      <Checkbox 
                        checked={selectedBundles.includes(bundle.id)}
                        onCheckedChange={() => toggleBundleSelection(bundle.id)}
                        data-testid={`bundle-checkbox-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{bundle.name}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[200px]">{bundle.lotBarcode}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getBundleTypeBadgeColor(bundle.bundleType)}>
                        {getBundleTypeLabel(bundle.bundleType)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{bundle.components?.length || 0} ürün</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <p className="font-mono font-medium">{formatPrice(bundle.price, bundle.currencyCode)}</p>
                        {bundle.components?.length > 0 && (() => {
                          const totalQty = bundle.components.reduce((sum, c) => sum + (c.quantity || 1), 0);
                          if (totalQty > 1) {
                            const unitPrice = (bundle.price / totalQty).toFixed(2);
                            return <p className="text-xs text-slate-400">{formatPrice(unitPrice, bundle.currencyCode)}/adet</p>;
                          }
                          return null;
                        })()}
                        {bundle.compareAtPrice > bundle.price && (
                          <p className="text-xs text-slate-400 line-through">{formatPrice(bundle.compareAtPrice, bundle.currencyCode)}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {bundle.discountType !== "none" && (
                        <Badge className="bg-red-500">
                          {bundle.discountType === "percent" ? `%${bundle.discountValue}` : formatPrice(bundle.discountValue, bundle.currencyCode)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {bundle.isActive ? (
                        <Badge className="bg-emerald-500">Aktif</Badge>
                      ) : (
                        <Badge variant="secondary">Pasif</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {(bundle.synced_to_shopify || bundle.shopifyProductId) ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-slate-300 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEditDialog(bundle)}>
                          <Pencil className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => syncBundleToShopify(bundle.id)}>
                          <ShoppingBag className="w-4 h-4 text-purple-500" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteBundle(bundle.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Toplam {totalBundles.toLocaleString('tr-TR')} asorti
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1 || loading}
                    data-testid="bundles-prev-page"
                  >
                    Önceki
                  </Button>
                  <span className="text-sm font-medium px-3">
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages || loading}
                    data-testid="bundles-next-page"
                  >
                    Sonraki
                  </Button>
                </div>
              </div>
            )}
          </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Bundle Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBundle ? "Paketi Düzenle" : "Yeni Asorti Paketi"}</DialogTitle>
            <DialogDescription>
              Birden fazla ürünü bir paket olarak satışa sunun
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Sol Panel - Paket Bilgileri */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bundleName">Paket Adı *</Label>
                <Input
                  id="bundleName"
                  value={bundleForm.name}
                  onChange={(e) => setBundleForm({...bundleForm, name: e.target.value})}
                  placeholder="Örn: 3'lü T-Shirt Seti"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Açıklama</Label>
                <Input
                  id="description"
                  value={bundleForm.description}
                  onChange={(e) => setBundleForm({...bundleForm, description: e.target.value})}
                  placeholder="Paket açıklaması..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Paket Türü</Label>
                  <Select 
                    value={bundleForm.bundleType} 
                    onValueChange={(v) => setBundleForm({...bundleForm, bundleType: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Sabit Paket</SelectItem>
                      <SelectItem value="multipack">Çoklu Paket (Aynı Ürün)</SelectItem>
                      <SelectItem value="mix-match">Seç & Karıştır</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>İndirim Türü</Label>
                  <Select 
                    value={bundleForm.discountType} 
                    onValueChange={(v) => setBundleForm({...bundleForm, discountType: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Yüzde (%)</SelectItem>
                      <SelectItem value="fixed">Sabit Tutar</SelectItem>
                      <SelectItem value="none">İndirim Yok</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {bundleForm.discountType !== "none" && (
                <div className="space-y-2">
                  <Label htmlFor="discountValue">
                    İndirim Değeri {bundleForm.discountType === "percent" ? "(%)" : "(Tutar)"}
                  </Label>
                  <Input
                    id="discountValue"
                    type="number"
                    value={bundleForm.discountValue}
                    onChange={(e) => setBundleForm({...bundleForm, discountValue: parseFloat(e.target.value) || 0})}
                  />
                </div>
              )}

              {/* Fiyat Özeti */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="font-medium text-slate-700">Fiyat Özeti</p>
                <div className="flex justify-between text-sm">
                  <span>Toplam Liste Fiyatı:</span>
                  <span className="font-mono">{formatPrice(totalPrice, bundleForm.currencyCode)}</span>
                </div>
                {bundleForm.discountType !== "none" && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>İndirim:</span>
                    <span className="font-mono">-{formatPrice(totalPrice - finalPrice, bundleForm.currencyCode)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-2">
                  <span>Paket Fiyatı:</span>
                  <span className="font-mono text-emerald-600">{formatPrice(finalPrice, bundleForm.currencyCode)}</span>
                </div>
              </div>

              {/* Miktar Bazlı Fiyatlandırma */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-700">Miktar Bazlı Fiyatlandırma</p>
                    <p className="text-xs text-slate-500">Farklı miktarlar için farklı fiyatlar belirleyin</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setBundleForm({
                      ...bundleForm, 
                      quantityPricing: [...bundleForm.quantityPricing, {quantity: 1, totalPrice: finalPrice}]
                    })}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Kırılım Ekle
                  </Button>
                </div>
                
                {bundleForm.quantityPricing.length > 0 ? (
                  <div className="space-y-2">
                    {bundleForm.quantityPricing.map((qp, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded p-2">
                        <div className="flex-1">
                          <Label className="text-xs">Miktar</Label>
                          <Input
                            type="number"
                            min="1"
                            value={qp.quantity}
                            onChange={(e) => {
                              const newQP = [...bundleForm.quantityPricing];
                              newQP[idx].quantity = parseInt(e.target.value) || 1;
                              setBundleForm({...bundleForm, quantityPricing: newQP});
                            }}
                            className="h-8"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs">Toplam Fiyat (₺)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={qp.totalPrice}
                            onChange={(e) => {
                              const newQP = [...bundleForm.quantityPricing];
                              newQP[idx].totalPrice = parseFloat(e.target.value) || 0;
                              setBundleForm({...bundleForm, quantityPricing: newQP});
                            }}
                            className="h-8"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs">Birim Fiyat</Label>
                          <div className="h-8 flex items-center text-sm font-mono text-emerald-600">
                            {qp.quantity > 0 ? (qp.totalPrice / qp.quantity).toFixed(2) : "0.00"} ₺/ad
                          </div>
                        </div>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 text-red-500"
                          onClick={() => {
                            const newQP = bundleForm.quantityPricing.filter((_, i) => i !== idx);
                            setBundleForm({...bundleForm, quantityPricing: newQP});
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-2">
                    Henüz fiyat kırılımı eklenmedi. Shopify'a tek fiyatla gönderilecek.
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Aktif</Label>
                  <p className="text-xs text-slate-500">Paketi satışa sun</p>
                </div>
                <Switch
                  checked={bundleForm.isActive}
                  onCheckedChange={(v) => setBundleForm({...bundleForm, isActive: v})}
                />
              </div>
            </div>

            {/* Sağ Panel - Ürün Seçimi */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Paket İçeriği ({bundleForm.components.length} ürün)</Label>
                <Button size="sm" variant="outline" onClick={() => setComponentDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ürün Ekle
                </Button>
              </div>

              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                {bundleForm.components.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">Henüz ürün eklenmedi</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {bundleForm.components.map((comp, idx) => (
                      <div key={idx} className="p-3 flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                          {comp.image ? (
                            <img src={comp.image} alt="" className="w-full h-full object-cover rounded" />
                          ) : (
                            <Box className="w-6 h-6 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{comp.productName}</p>
                          <p className="text-xs text-slate-500">{comp.productCode}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => updateComponentQuantity(comp.productCode, comp.quantity - 1)}
                          >
                            -
                          </Button>
                          <span className="w-8 text-center font-mono">{comp.quantity}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => updateComponentQuantity(comp.productCode, comp.quantity + 1)}
                          >
                            +
                          </Button>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <p className="font-mono text-sm">{(comp.price * comp.quantity).toFixed(2)} ₺</p>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => removeComponent(comp.productCode)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            {editingBundle && (
              <Button 
                onClick={syncCurrentBundleToShopify} 
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Shopify'a Aktar
              </Button>
            )}
            <Button onClick={saveBundle} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingBundle ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog open={componentDialogOpen} onOpenChange={setComponentDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Ürün Seç</DialogTitle>
            <DialogDescription>Pakete eklenecek ürünleri seçin</DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingProducts ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto divide-y">
                {products.slice(0, 50).map((product, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer"
                    onClick={() => {
                      addComponent(product);
                      toast.success(`${product.productName || product.productCode} eklendi`);
                    }}
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                      <Box className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{product.productName || product.productCode}</p>
                      <p className="text-xs text-slate-500">{product.productCode}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm">{product.price?.toFixed(2) || "0.00"} ₺</p>
                    </div>
                    <Plus className="w-4 h-4 text-emerald-500" />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setComponentDialogOpen(false)}>Tamam</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
